import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import { Link, unstable_usePrompt as usePrompt } from 'react-router-dom'
import { Grid } from '@mui/material'

import {
  DEFAULT_USER_AVATAR,
  REVENUE_CAT_ENV_STRING,
  REVENUE_CAT_URL,
  USER_MAPPING_FED_PROVIDER,
  userProviderMap,
} from 'config'
import FormSectionContainer from 'components/layout/FormSectionContainer'
import InputField from 'components/form/InputField'
import HcpField from 'components/form/HcpField'
import DateTimePicker from 'components/form/DateTimePicker'
import CountrySelect from 'components/form/CountrySelect'
import ClubSelect from 'components/form/ClubSelect'
import GenericSelect from 'components/form/GenericSelect'
import SearchButton from 'components/form/SearchButton'
import SubmitButton from 'components/form/SubmitButton'
import SwitchToggle from 'components/form/SwitchToggle'
import FileUpload from 'components/form/FileUpload'
import UserMemberships from 'components/user/UserPrivileges'

import { forgotPassword } from 'store/authentication/thunks'
import { updateUser, deleteUserImage, deleteUser } from 'store/user/thunks'
import { convertHcpToString } from 'utils/convertHcpToString'
import { formatDate } from 'utils/formatDate'
import TextArea from 'components/form/TextArea'

interface Props {
  userState: UserState
  formStatus: (arg0: boolean) => void
}

export default function UserEditForm({ userState, formStatus }: Props): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [initialFormDataAdded, setInitialFormDataAdded] = useState<boolean>(false)
  const [formDataUpdated, setFormDataUpdated] = useState<boolean>(false)
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false)
  const [initialFormData, setInitialFormData] = useState<any>({
    firstName: '',
    lastName: '',
    profileDescription: '',
    email: '',
    account: '',
    gender: null,
    temporary: false,
    country: null,
    clubCountry: null,
    club: null,
    hcp: '',
    birthDate: '',
    userNote: '',
  })
  const [fedLinks, setFedLinks] = useState<UserLinkedAccount[]>([])

  const { user, userLinkedAccounts, updatingStatus, resetingPasswordStatus, deletingUserStatus } = userState

  const { handleSubmit, control, register, reset, watch, formState } = useForm({
    defaultValues: initialFormData,
    shouldUnregister: false,
  })

  const userId = userState.user.id
  const linkTo = `${REVENUE_CAT_URL}${userId}${REVENUE_CAT_ENV_STRING}`

  useEffect(() => {
    const { dirtyFields } = formState
    const dirtyFieldsLength = Object.keys(dirtyFields).length

    // No need to consider clubCountry as that is never saved anywhere
    if (dirtyFieldsLength === 1 && 'clubCountry' in dirtyFields) {
      setIsFormDirty(false)
      formStatus(false)
      return
    }
    const isDirty = dirtyFieldsLength > 0
    setIsFormDirty(isDirty)
    formStatus(isDirty)
  }, [formState, formStatus])

  useEffect(() => {
    if ((user.id && !initialFormDataAdded) || (updatingStatus === 'succeeded' && !formDataUpdated)) {
      const country =
        user.country?.id && user.country?.name ? { label: user.country?.name, key: user.country?.id } : null

      const club = user.club?.id && user.club?.name ? { label: user.club?.name, key: user.club?.id } : null
      const clubCountry =
        user.club?.country?.id && user.club?.country?.name
          ? { label: user.club?.country?.name, key: user.club?.country?.id }
          : null

      const initialData = {
        firstName: user.firstName,
        lastName: user.lastName,
        profileDescription: user.profileDescription,
        email: user.email,
        account: user.account,
        gender: user.gender,
        temporary: user.temporary,
        country: country,
        club: club,
        clubCountry: clubCountry,
        hcp: convertHcpToString(user.handicap),
        birthDate: formatDate(new Date(user.dateOfBirth?.date)),
        userNote: user.userNote,
      }

      setInitialFormData(initialData)
      setInitialFormDataAdded(true)
      reset(initialData)
      if (updatingStatus === 'succeeded') {
        setFormDataUpdated(true)
      }
    }
  }, [user, initialFormData, reset, updatingStatus, initialFormDataAdded, formDataUpdated])

  useEffect(() => {
    setFedLinks(
      userLinkedAccounts.filter((account: UserLinkedAccount) =>
        Object.values(USER_MAPPING_FED_PROVIDER).includes(account.provider as USER_MAPPING_FED_PROVIDER),
      ),
    )
  }, [userLinkedAccounts])

  const leavePageConfirmationMessage = () => {
    return intl.formatMessage({ id: 'strings.leavePageConfirmation' })
  }

  useEffect(() => {
    function confirmLeaving(event: any) {
      if (isFormDirty) {
        event.preventDefault()
        return (event.returnValue = leavePageConfirmationMessage())
      }
      return
    }
    window.addEventListener('beforeunload', confirmLeaving)

    return () => {
      window.removeEventListener('beforeunload', confirmLeaving)
    }
  })

  usePrompt({ when: isFormDirty, message: leavePageConfirmationMessage() })

  const genderOptions = [
    { key: 'male', label: intl.formatMessage({ id: 'strings.male' }) },
    { key: 'female', label: intl.formatMessage({ id: 'strings.female' }) },
    { key: 'unknown', label: intl.formatMessage({ id: 'strings.unknown' }) },
  ]

  const submitForm = (data: any) => {
    const {
      firstName,
      lastName,
      profileDescription,
      email,
      account,
      gender,
      country,
      club,
      hcp,
      birthDate,
      temporary,
      userNote,
    } = data
    let newHcp = hcp ? hcp : watch('hcp')
    newHcp = newHcp.includes('+') ? parseFloat(newHcp) : parseFloat(`-${newHcp}`)

    if (user.id) {
      const payload: UpdateUserPayload = {
        id: user.id,
        successMessage: intl.formatMessage({ id: 'strings.userUpdatedSuccessfully' }),
        data: {
          firstName,
          lastName,
          profileDescription,
          email,
          account,
          gender,
          countryId: country?.key,
          homeClubId: club?.key,
          hcp: newHcp,
          birthday: birthDate,
          isTemporary: temporary,
          userNote: userNote,
        },
      }
      dispatch(updateUser(payload))
      setFormDataUpdated(false)
    }
  }

  const deleteImage = (imageType: 'avatar' | 'cover') => {
    const confirmText = intl.formatMessage({ id: 'strings.deletePhotoConfirmation' })
    if (window.confirm(confirmText)) {
      if (user.id) {
        const payload = {
          id: user.id,
          imageType,
        }
        dispatch(deleteUserImage(payload))
      }
    }
  }

  const resetPassword = () => {
    const confirmText = intl.formatMessage({ id: 'strings.resetPasswordForUserConfirmation' })
    if (window.confirm(confirmText)) {
      if (user.email) {
        const payload: ForgotPasswordPayload = {
          successMessage: intl.formatMessage({ id: 'strings.passwordResetEmailSentToUser' }),
          email: user.email,
        }
        dispatch(forgotPassword(payload))
      }
    }
  }

  const deleteUserProfile = () => {
    const confirmText = intl.formatMessage({ id: 'strings.confirmUserDelete' })
    if (window.confirm(confirmText)) {
      if (user.id) {
        dispatch(
          deleteUser({ id: user.id, successMessage: intl.formatMessage({ id: 'strings.userDeletedSuccessfully' }) }),
        )
      }
    }
  }

  const isDefaultAvatarImage = () => {
    return user.avatarUrl === DEFAULT_USER_AVATAR
  }

  const getUserLinkedAccountsLabel = () => {
    const baseLabel = intl.formatMessage({ id: 'strings.fedLinking' })
    if (fedLinks.length > 0) {
      const providers = fedLinks.map((f: UserLinkedAccount) => userProviderMap.get(f.provider)).flat()
      return `${baseLabel} (${providers.join(',')})`
    }

    return baseLabel
  }

  return (
    <>
      <FormSectionContainer>
        <form onSubmit={handleSubmit(submitForm)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SectionTitle>{intl.formatMessage({ id: 'strings.personalInformation' })}</SectionTitle>
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <PersonalInfoContainer item xs={7}>
              <InputField
                register={register}
                name="firstName"
                type="text"
                label={intl.formatMessage({ id: 'strings.firstName' })}
                outlined
                required
              />

              <InputField
                register={register}
                name="lastName"
                type="text"
                label={intl.formatMessage({ id: 'strings.lastName' })}
                outlined
                required
              />

              <InputField
                register={register}
                name="account"
                type="text"
                label={intl.formatMessage({ id: 'strings.account' })}
                outlined
                required
              />

              <InputField
                register={register}
                name="email"
                type="email"
                label={intl.formatMessage({ id: 'strings.email' })}
                outlined
                required
              />

              <GenericSelect
                name="gender"
                label={intl.formatMessage({ id: 'strings.gender' })}
                control={control}
                options={genderOptions}
                initialValue={watch('gender')}
                required
              />

              <CountrySelect
                name="country"
                control={control}
                initialValue={watch('country')}
                label={intl.formatMessage({ id: 'strings.country' })}
              />

              <DateTimePicker
                register={register}
                name="birthDate"
                label={intl.formatMessage({ id: 'strings.birthDate' })}
                dateOnly
                fullWidth
                required
              />
              <InputField
                register={register}
                name="profileDescription"
                type="text"
                label={intl.formatMessage({ id: 'strings.descriptionText' })}
                outlined
              />
              <SwitchToggle
                control={control}
                name="temporary"
                label={intl.formatMessage({ id: 'strings.isTemporary' })}
                value={user.temporary}
                disabled={!user.temporary}
              />
            </PersonalInfoContainer>
            <PhotosContainer item xs={5}>
              <FileUpload
                name="avatarImage"
                suggestedWidth={300}
                suggestedHeight={300}
                buttonText={intl.formatMessage({ id: 'strings.uploadImage' })}
                allowedFileTypes="PNG, JPG"
                s3Url={user.avatarUrl || ''}
                fileType="avatar"
                circularImage
                deleteFile={!isDefaultAvatarImage() ? () => deleteImage('avatar') : undefined}
                disabled
                showDescription={false}
                imagePosition="bottom"
              />
              <FileUpload
                name="coverImage"
                suggestedWidth={1920}
                suggestedHeight={1200}
                buttonText={intl.formatMessage({ id: 'strings.uploadImage' })}
                allowedFileTypes="PNG, JPG"
                s3Url={user.coverImgUrl || ''}
                fileType="cover"
                deleteFile={() => deleteImage('cover')}
                disabled
                showDescription={false}
                imagePosition="bottom"
              />
            </PhotosContainer>

            <Spacer />
            <DividerLine />
            <Spacer />

            <Grid item xs={12}>
              <SectionTitle>{intl.formatMessage({ id: 'strings.golfInformation' })}</SectionTitle>
            </Grid>

            <Grid container spacing={3}>
              <GolfInfoContainer item xs={12}>
                <CountrySelect
                  name="clubCountry"
                  control={control}
                  onlyCountriesWithClubs
                  initialValue={watch('clubCountry')}
                  label={intl.formatMessage({ id: 'strings.homeClubCountry' })}
                />
                <ClubSelect
                  name="club"
                  control={control}
                  initialValue={watch('club')}
                  countryId={watch('clubCountry')?.key}
                />
                <StyledHcpField register={register} name="hcp" outlined value={watch('hcp')} required />
                <SwitchToggle
                  name="fedLink"
                  label={getUserLinkedAccountsLabel()}
                  value={fedLinks.length > 0}
                  disabled
                />
              </GolfInfoContainer>
            </Grid>

            <Spacer />
            <DividerLine />
            <Spacer />

            <Grid item xs={12}>
              <UserMemberships userState={userState} />
            </Grid>

            <Spacer />
            <DividerLine />
            <Spacer />

            <Grid item xs={12}>
              <SectionTitle>{intl.formatMessage({ id: 'strings.transactions' })}</SectionTitle>
              <Grid container spacing={3}>
                <Grid item xs={2}>
                  {userId && (
                    <StyledLink to={linkTo} target="_blank">
                      {intl.formatMessage({ id: 'strings.linkToRevenueCatProfile' })}
                    </StyledLink>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <TextArea
                    name="userNote"
                    register={register}
                    height={70}
                    label={<FormattedMessage id="strings.userNote" />}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Spacer />
            <DividerLine />
            <Spacer />

            <Grid item xs={12}>
              <SubmitButton
                status={updatingStatus}
                text={intl.formatMessage({ id: 'strings.save' })}
                disabled={!isFormDirty}
              />
            </Grid>
            <Grid item xs={12} style={{ marginTop: '20px' }}>
              <ResetPasswordButton
                status={resetingPasswordStatus}
                onClick={resetPassword}
                text={intl.formatMessage({ id: 'strings.resetPassword' })}
                disabled={!user.email}
              />
              <DeleteUserButton
                status={deletingUserStatus}
                onClick={deleteUserProfile}
                text={intl.formatMessage({ id: 'strings.deleteUser' })}
                disabled={!user.id}
              />
            </Grid>
          </Grid>
        </form>
      </FormSectionContainer>
    </>
  )
}

const SectionTitle = styled.h2`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const Spacer = styled.div`
  width: 100%;
  height: 40px;
`

const DividerLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.primary.main};
`

const PersonalInfoContainer = styled(Grid)`
  & > div {
    margin-bottom: 20px;

    & .MuiInputBase-formControl {
      background-color: ${(props) => props.theme.customPalette.lightGray};
    }

    &:first-of-type,
    &:nth-of-type(2),
    &:nth-of-type(3),
    &:nth-of-type(4) {
      width: calc(50% - 5px);
    }

    &:nth-of-type(5) {
      width: calc(20% - 5px);
      margin-top: 15px;
      & > .MuiInputBase-formControl {
        height: 55px;
      }
    }

    &:nth-of-type(6) {
      width: calc(40% - 5px);
      margin-right: 5px;
      margin-top: 15px;
    }

    &:nth-of-type(7) {
      width: calc(30% - 5px);
      margin-right: 0;
      margin-left: 5px;
      & > .MuiInputBase-formControl {
        height: 55px;
      }
    }

    &:nth-of-type(odd) {
      margin-right: 5px;
    }
    &:nth-of-type(even) {
      margin-left: 5px;
    }
    &:last-of-type {
      margin-left: 0;
      margin-right: 0;
    }
  }
`

const PhotosContainer = styled(Grid)`
  display: flex;
  flex-wrap: wrap;
  & > div {
    width: 50%;
  }
`

const StyledHcpField = styled(HcpField)`
  align-items: flex-start;
`

const GolfInfoContainer = styled(Grid)`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px;

  & > div {
    margin-top: 16px;
    margin-bottom: 8px;

    & label {
      padding: 0;
    }

    & .MuiInputBase-formControl {
      background-color: ${(props) => props.theme.customPalette.lightGray};
      height: 56px;
      box-sizing: border-box;
    }

    &:first-of-type {
      width: calc(20% - 5px);
      margin-left: 25px;
    }

    &:nth-of-type(2) {
      width: calc(25% - 5px);
    }

    &:nth-of-type(3) {
      width: calc(10% - 5px);
    }

    &:nth-of-type(4) {
      height: 56px;
      justify-content: center;
      margin-left: 25px;
    }
  }
`

const ResetPasswordButton = styled(SearchButton)`
  margin-bottom: 20px;
  margin-right: 20px;
`

const DeleteUserButton = styled(SearchButton)`
  margin-bottom: 20px;
  background-color: ${(props) => props.theme.customPalette.error};

  &:hover {
    background-color: ${(props) => props.theme.customPalette.error};
    opacity: 0.85;
  }
`
const StyledLink = styled(Link)`
  color: green;
  text-decoration: none;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
  &:focus {
    outline: 2px solid green;
    outline-offset: 2px;
  }
`
